import type { ComponentType } from 'react';
import {
  ShieldSlashIcon,
  SparkleIcon,
  TagIcon,
  TimerIcon,
  WarningIcon,
} from '@phosphor-icons/react';
import type { FlightOfferSource } from '../flight_offer_source.js';
import type { FlightAirportSwitchView, FlightOfferCopy } from './offer_copy.js';

type OfferBadge = NonNullable<FlightOfferSource['badges']>[number];
type DrawnAxis = keyof FlightOfferCopy['badge'];

/**
 * Os eixos que o produtor realmente emite. O enum do contrato tem seis valores
 * (`flex`/`direct`/`alt` sobraram de um desenho anterior e nunca tiveram
 * produtor); um `kind` sem entrada aqui não pinta selo em vez de pintar um selo
 * genérico errado.
 *
 * Todos são Phosphor, como manda a regra de ícones do DS (#3874). O
 * recomendado leva o Sparkle preenchido, o mesmo da nota da IA logo abaixo no
 * card: é o sinal de "escolha do agente". Já foi o V da VOLL, mas o cliente lia
 * a marca como recomendação ou tarifa DA VOLL, e não da política da empresa
 * dele (#4274).
 *
 * `glyphSize` é o da linha DESKTOP (`.fq-flag`); o card do celular usa o seu
 * (14 para etiqueta e cronômetro) — ver `offer_card_stack.tsx`. O Sparkle é 12
 * nos dois: é o tamanho do selo Recomendado em qualquer superfície.
 */
function AgentPickIcon({ size }: { readonly size?: number }) {
  return <SparkleIcon size={size} weight="fill" aria-hidden />;
}

const AXIS: Readonly<
  Record<DrawnAxis, { Glyph: ComponentType<{ size?: number }>; glyphSize: number; soft: boolean }>
> = {
  best: { Glyph: AgentPickIcon, glyphSize: 12, soft: false },
  cheap: { Glyph: TagIcon, glyphSize: 12, soft: true },
  fast: { Glyph: TimerIcon, glyphSize: 12, soft: true },
};

/** O desenho de um eixo e a palavra dele, ou `undefined` para um eixo que a
 *  tela não desenha (o produtor pode mandar um antes desta casca). */
export function axisOf(
  kind: string,
  copy: FlightOfferCopy,
): ((typeof AXIS)[DrawnAxis] & { text: string }) | undefined {
  if (kind !== 'best' && kind !== 'cheap' && kind !== 'fast') return undefined;
  return { ...AXIS[kind], text: copy.badge[kind] };
}

/**
 * **Selo do eixo** — por que ESTE voo está na lista. Um só tratamento com duas
 * variantes, como o desenho: o recomendado é preenchido em tinta e carrega o
 * Sparkle (quem escolhe é o agente, e dizer isso pelo glifo libera o texto para
 * dizer o que o selo É); os outros dois são claros, com etiqueta e cronômetro.
 *
 * **Cor não codifica o eixo.** Verde para barato e azul para rápido seria uma
 * segunda escala de cor competindo com a de política, que é a única do card com
 * direito a alarme. O que separa os três é o glifo e a palavra.
 *
 * **O texto vem daqui, não do wire.** O wire manda `kind`, que é o dado; o
 * rótulo é decisão de UI e sai do i18n, então ele fala o idioma de quem LÊ o
 * card. Antes vinha em `label` da tabela do produtor — o que fazia um leitor
 * espanhol receber rótulo em português (a tabela de lá tem o `es.best` em
 * pt-BR), e amarrava mudança de palavra a deploy do agents. O `label` segue no
 * contrato porque bundles antigos ainda o leem; este renderer ignora.
 */
export function AxisFlag({
  badge,
  copy,
}: {
  readonly badge: OfferBadge;
  readonly copy: FlightOfferCopy;
}) {
  const spec = axisOf(badge.kind, copy);
  // Eixo sem desenho (nenhum produtor emite `flex`/`direct`/`alt` hoje, e um
  // valor novo pode chegar antes desta casca): cai no rótulo do wire com o
  // tratamento claro e sem glifo — o Sparkle é do recomendado e não pode
  // marcar um eixo que ninguém desenhou. Dropar o selo perderia a informação
  // que o produtor mandou — degradar é melhor que sumir.
  if (!spec) {
    return <span className="fq-flag fq-flag--soft">{badge.label}</span>;
  }
  const Glyph = spec.Glyph;
  return (
    <span className={`fq-flag${spec.soft ? ' fq-flag--soft' : ''}`}>
      <Glyph size={spec.glyphSize} />
      {spec.text}
    </span>
  );
}

/**
 * **Ressalva de política — selo com palavra.** Marca só o problema: renderizado
 * apenas para voo FORA da política, e é a porta do drawer com o motivo.
 *
 * Antes era um `ShieldOff` de 16px, mudo, na linha da legenda do preço. Um
 * marcador que só se explica por interação não marca — em WebView não existe
 * hover, e a exceção mais cara do card era o elemento mais fácil de não ver.
 * Agora ele tem a palavra e sobe para o eixo dos selos.
 *
 * **Mesmo eixo do selo de curadoria, canto oposto.** Recomendação à esquerda,
 * ressalva à direita, as duas interrompendo a borda do topo — uma gramática só
 * para "o que esta linha tem de especial". Continuam sendo CANAIS SEPARADOS: o
 * selo do eixo nunca fica vermelho, e o de política nunca vira eixo.
 *
 * Sem `onOpenPolicy` o selo vira texto e não botão: sem a porta do drawer não
 * há nada a mais para mostrar, e a palavra já É a explicação que cabe no card.
 */
export function PolicyFlag<O extends FlightOfferSource>({
  offer,
  copy,
  onOpenPolicy,
}: {
  readonly offer: O;
  readonly copy: FlightOfferCopy;
  readonly onOpenPolicy?: ((offer: O) => void) | undefined;
}) {
  const label = copy.outPolicy;
  if (!onOpenPolicy) {
    return (
      <span className="fq-flag fq-flag--policy">
        <ShieldSlashIcon size={12} aria-hidden />
        {label}
      </span>
    );
  }
  return (
    <button
      type="button"
      className="fq-flag fq-flag--policy"
      aria-label={copy.outPolicyAria}
      onClick={(e) => {
        // O selo mora dentro de uma linha que é ela mesma alvo de clique: sem
        // parar aqui, pedir a explicação escolheria a oferta.
        e.stopPropagation();
        onOpenPolicy(offer);
      }}
    >
      <ShieldSlashIcon size={12} aria-hidden />
      {label}
    </button>
  );
}

/**
 * **De onde a volta parte (#4203)**, quando não é o aeroporto em que a ida
 * chega. A cidade embaixo do código não desempata CGH × GRU ("São Paulo" nos
 * dois), então a linha diz o aeroporto por extenso. Pedida a cidade inteira
 * (`expected`), é só rótulo; senão, aviso em âmbar com o aeroporto da ida.
 */
export function AirportSwitchTag({
  found,
  copy,
}: {
  readonly found: FlightAirportSwitchView | null | undefined;
  readonly copy: FlightOfferCopy;
}) {
  if (!found) return null;
  if (found.expected) {
    return (
      <p className="m-0 pt-1.5 text-detail text-muted-foreground">
        {copy.airportSwitch.rowExpected(found.arrival, found.departure)}
      </p>
    );
  }
  return (
    <p className="m-0 mt-1.5 flex items-start gap-1 rounded-md bg-warning-soft px-2 py-1 text-detail font-medium text-warning-strong">
      <WarningIcon size={14} className="mt-0.5 shrink-0" aria-hidden />
      {copy.airportSwitch.row(found.arrival, found.departure)}
    </p>
  );
}
